import INotificationProps from '../dtos/INotificationProps';
import iconLikeProjectDefault from '../assets/images/notification-like-icon.png';
import iconCommentsProjectDefault from '../assets/images/notification-comment-icon.png';
import iconPointsProjectsDefault from '../assets/images/notification-points-icon.png';

export interface INotificationItem extends INotificationProps {
  link?: string;
  icon_url?: string;
}

/**
 * `type` da notificação que convida o usuário a trocar a foto de perfil (quem
 * nunca trocou). Precisa bater EXATAMENTE com o valor emitido pelo backend —
 * um type desconhecido não quebra a lista, mas cai no ícone genérico e perde o
 * link para a tela de edição, que é o ponto inteiro da notificação.
 */
export const AVATAR_REMINDER_TYPE = 'avatar-reminder';

/** Rota onde o input de troca de avatar vive (ver pages/Profile). */
export const AVATAR_EDIT_PATH = '/profile';

/**
 * Faz o parse seguro do metadata de uma notificação.
 * Retorna o objeto parsed ou null se falhar.
 */
export function parseMetadata(
  metadata: unknown,
): Record<string, unknown> | null {
  if (!metadata) return null;
  try {
    return typeof metadata === 'string'
      ? JSON.parse(metadata)
      : (metadata as Record<string, unknown>);
  } catch {
    return null;
  }
}

/**
 * Extrai um campo do metadata, verificando múltiplos caminhos possíveis.
 */
export function extractMetadataField(
  metadataObj: Record<string, unknown>,
  fieldName: string,
  ...alternativeFields: string[]
): string | undefined {
  const nested = metadataObj.metadata as Record<string, unknown> | undefined;
  const value = nested?.[fieldName] ?? metadataObj[fieldName];

  if (value) return String(value);

  for (const alt of alternativeFields) {
    const altValue = nested?.[alt] ?? metadataObj[alt];
    if (altValue) return String(altValue);
  }

  return undefined;
}

/**
 * Mapeia uma notificação crua para uma INotificationItem com link e ícone.
 *
 * Compartilhado entre o painel do header e a página /profile/notifications
 * para que um type novo só precise ser tratado em um lugar.
 */
export function mapNotificationToItem(
  notification: INotificationProps,
): INotificationItem {
  const { type, metadata } = notification;

  // Badge / Level → link para conquistas
  if (type === 'badge' || type === 'level') {
    const metadataObj = parseMetadata(metadata);
    const badgeIcon = metadataObj
      ? extractMetadataField(metadataObj, 'badge_icon')
      : undefined;

    return {
      ...notification,
      link: '/profile/achievements',
      ...(badgeIcon && { icon_url: badgeIcon }),
    };
  }

  // Curtida ou comentário em projeto → link para o post
  if (type === 'project-liked' || type === 'project-commented') {
    const metadataObj = parseMetadata(metadata);
    const postId = metadataObj
      ? extractMetadataField(metadataObj, 'post_id', 'activity_id')
      : undefined;

    const icon =
      type === 'project-liked'
        ? iconLikeProjectDefault
        : iconCommentsProjectDefault;

    if (postId) {
      return {
        ...notification,
        link: `/community/post/${postId}`,
        icon_url: icon,
      };
    }

    return notification;
  }

  // Convite a trocar a foto de perfil → link para a tela que tem o input.
  // Sem o link a notificação vira só um aviso, e o usuário teria que
  // descobrir sozinho onde se troca o avatar.
  if (type === AVATAR_REMINDER_TYPE) {
    return {
      ...notification,
      link: AVATAR_EDIT_PATH,
      icon_url: iconPointsProjectsDefault,
    };
  }

  // Pontos — inclui a premiação por ter trocado o avatar, que o backend emite
  // com este mesmo type.
  if (type === 'points') {
    return {
      ...notification,
      icon_url: iconPointsProjectsDefault,
    };
  }

  return notification;
}

export { iconLikeProjectDefault as defaultNotificationIcon };
